import axios from 'axios';
import {store} from '../app/store';
import {flushPersistedState} from '../app/persistorRef';
import {logoutUser} from '../features/user/userSlice';
import {invalidateMix} from './mixCache';
import {notifyBackendError} from './backendErrorHandler';

// LOCALHOST CONFIG
//axios.defaults.baseURL = 'http://108.230.241.172:8080/api/v1'; //BRANDON
//axios.defaults.baseURL = 'http://192.168.11.128:8080/api/v1';
//axios.defaults.baseURL = 'http://192.168.1.90:8080/api/v1';

// PROD CONFIGURATION
//axios.defaults.baseURL = 'https://hostlinguere.net:8443/api/v1';
//axios.defaults.baseURL = 'https://ivorymontessorisystem.com:8443/api/v1';

// LOCALHOST CONFIG AWS (relative => proxied by CRA dev server via package.json "proxy")
axios.defaults.baseURL = 'https://ivorymontessorisystem.com/imsv3/api/v1';

//axios.defaults.headers.post["Content-Type"] = "application/json";

/**
 * The browser authenticates with an httpOnly cookie issued by the gateway at
 * login. `withCredentials` is what makes axios attach that cookie to
 * cross-origin calls (dev: localhost:3000 -> localhost:8222; prod:
 * erp.ivorymontessorisystem.com -> ivorymontessorisystem.com).
 *
 * The token is deliberately NOT kept in JavaScript-reachable storage anymore: an
 * httpOnly + Secure + SameSite cookie cannot be read by an XSS payload, which is
 * the OWASP-recommended answer. Mobile clients are unaffected — they keep using
 * `Authorization: Bearer` and the gateway still returns the token in the login
 * response body.
 */
axios.defaults.withCredentials = true;

/**
 * Legacy token purge. Earlier releases stored the JWT in the browser, first in
 * localStorage then in sessionStorage. Run once at boot so an upgrade does not
 * leave a reusable token behind for a wildcard XSS payload or a shared machine.
 */
try {
    window.localStorage.removeItem("auth_token");
    window.sessionStorage.removeItem("auth_token");
} catch {
    // Storage unavailable (privacy mode): nothing to purge.
}

/**
 * Whether the app believes the user has an active session.
 *
 * The authoritative session now lives in the httpOnly cookie, which JavaScript
 * cannot read; this flag (persisted per tab) only drives routing/UI. If the
 * cookie is missing or expired, the next API call returns 401 and the response
 * interceptor logs the user out.
 */
export const hasActiveSession = (): boolean => {
    try {
        return Boolean(store.getState()?.user?.isLoggedIn);
    } catch {
        return false;
    }
};

/**
 * Asks the gateway to expire the httpOnly auth cookie. The browser cannot delete
 * an httpOnly cookie on its own, so this server round-trip is the only way to end
 * the session. Best-effort: local state is cleared regardless of the outcome.
 */
export const clearAuthCookie = () => {
    if (!hasActiveSession()) {
        return;
    }
    axios.post('/public/auth/logout').catch(() => undefined);
};

/**
 * Clears the stale session and sends the user back to the login page.
 *
 * Idempotent on purpose: a single expired session is usually detected by several
 * concurrent requests at once (plus the WebSocket retry loop), and each one used
 * to call this helper. Navigating again while already sitting on the login page
 * reloads it, which discards the "session expired" notice before the user can
 * read it. Only leave the current page.
 */
export const logoutExpiredSession = () => {
    // Ends the session server-side (the cookie is httpOnly) and locally.
    clearAuthCookie();
    store.dispatch(logoutUser());
    window.localStorage.setItem('sessionExpired', '1');
    if (window.location.pathname !== '/') {
        // Wait for the cleared state to reach sessionStorage: a hard navigation
        // can otherwise discard the debounced write and the next boot would
        // rehydrate a logged-in user whose cookie is gone.
        void flushPersistedState().finally(() => {
            if (window.location.pathname !== '/') {
                window.location.href = '/';
            }
        });
    }
};

/**
 * A few business endpoints intentionally answer 401 for a bad credential or a
 * refused operation (wrong current password, permission mismatch). Those are
 * not expired sessions and must not destroy the authenticated session.
 */
const isBusinessUnauthorized = (error: any): boolean => {
    const data = error?.response?.data;
    const codeMessage = data?.codeMessage;
    const message = data?.message;
    return codeMessage === 'BAD_CREDENTIALS'
        || message === 'BAD_CREDENTIALS'
        || codeMessage === 'PERMISSION_EXCEPTION'
        || message === 'PERMISSION';
};

axios.interceptors.response.use(
    (response) => {
        // A successful write to a business endpoint can change the reference data
        // served by /corebase/mix/general/* (classes, profils, enfants, employees,
        // scolarite/cantine collections). Drop the cached mix payloads so the next
        // page load re-fetches fresh data instead of serving a stale TTL hit.
        const method = String(response?.config?.method ?? '').toUpperCase();
        const url = String(response?.config?.url ?? '');
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
            && (url.startsWith('/corebase') || url.startsWith('/extra'))) {
            invalidateMix();
        }
        return response;
    },
    (error) => {
        if (error?.response?.status === 401
            && hasActiveSession()
            && !isBusinessUnauthorized(error)) {
            // Let the login page explain why the user was logged out.
            logoutExpiredSession();
        } else {
            // Central backend exception handling (CustomException & friends):
            // resolve the friendly localized message and show the global snackbar.
            notifyBackendError(error);
        }
        return Promise.reject(error);
    }
);

export const request = (method: any, url: any, data: any) => {
    return axios({
        method: method,
        url: url,
        data: data,
    });
}

export const uploadFileRequest = (method: any, url: any, data: any) => {
    return axios({
        method: method,
        headers: {"Content-Type": "multipart/form-data"},
        url: url,
        data: data,
    })
}

export const uploadFileRequestAsync = async (method: any, url: any, data: any) => {
    return await axios({
        method: method,
        headers: {"Content-Type": "multipart/form-data"},
        url: url,
        data: data,
    })
}


export const getRequest = async (url:any) => {
    const response = await axios.get(url);
    return response.data;
}

export const postRequest = async (url:string, data:object) => {
    return await axios.post(url, data);
}

export const putRequest = async (url:string, data:object) => {
    return await axios.put(url, data);
}

export const deleteRequest = async (url:string, data:any) => {
    return await axios.delete(url, {data});
}

export const uploadFilePostRequest = async (url:any, data:any) => {
    return await axios.post(url, data, {headers: {"Content-Type": "multipart/form-data"}});
}


export const getData = async (url:any) => {
    const response = await axios.get(url);
    return response.data;
};
